/**
 * AC-M5-8 — Promoted entities section on session page
 *
 * Shows a compact list of goals/outcomes/knowledge docs that were
 * promoted from this session. Rendered at the bottom of the chat view.
 */
import React, { useState, useEffect } from 'react';
import { Target, TrendingUp, BookOpen, ExternalLink } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { t } from '@/lib/i18n';
import { agentSessionsService } from '@/services/roros/agent-sessions.service';
import type { PromotedEntitySummary } from '@/services/roros/agent-sessions.service';
import { useNavigate } from 'react-router-dom';

const ENTITY_CONFIG: Record<string, { icon: React.ElementType; label: string; path: (id: string) => string }> = {
  goal:          { icon: Target,      label: 'Goal',         path: (id) => `/roros/goals/${id}` },
  outcome:       { icon: TrendingUp,  label: 'Outcome',      path: (id) => `/roros/outcomes/${id}` },
  knowledge_doc: { icon: BookOpen,    label: 'Knowledge Doc', path: (id) => `/roros/knowledge/${id}` },
};

interface PromotedEntitiesSectionProps {
  sessionId: string;
  /** Updated from outside whenever a new entity is promoted, to force refresh */
  refreshToken?: number;
}

export function PromotedEntitiesSection({ sessionId, refreshToken }: PromotedEntitiesSectionProps) {
  const [entities, setEntities] = useState<PromotedEntitySummary[]>([]);
  const navigate = useNavigate();

  useEffect(() => {
    agentSessionsService.getPromotedEntities(sessionId)
      .then(setEntities)
      .catch(() => setEntities([]));
  }, [sessionId, refreshToken]);

  if (entities.length === 0) return null;

  return (
    <div className="mt-4 pt-4 border-t border-slate-100" data-testid="promoted-entities-section">
      <p className="text-xs font-medium text-slate-500 uppercase tracking-wider mb-2">
        {t('Promoted Entities')}
      </p>
      <div className="flex flex-wrap gap-1.5">
        {entities.map((e) => {
          const cfg = ENTITY_CONFIG[e.entityType] ?? ENTITY_CONFIG.goal;
          const Icon = cfg.icon;
          return (
            <Button
              key={e.id}
              variant="ghost"
              onClick={() => navigate(cfg.path(e.entityId))}
              className="inline-flex items-center gap-1.5 px-2.5 py-1 h-auto text-xs bg-slate-50 border border-slate-200 rounded-full text-slate-700 hover:bg-slate-100 active:bg-slate-100 hover:text-slate-700 transition-colors max-w-[200px] whitespace-nowrap"
              title={e.entityTitle ?? cfg.label}
              data-testid="promoted-entity-chip"
            >
              <Icon className="h-3 w-3 flex-shrink-0 text-slate-500" />
              <span className="truncate">{e.entityTitle ?? cfg.label}</span>
              <ExternalLink className="h-2.5 w-2.5 flex-shrink-0 text-slate-400" />
            </Button>
          );
        })}
      </div>
    </div>
  );
}
